"use client";
import React, { useState } from "react";
import Image from "next/image";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";
import { ScrollProgressBar } from "./ProgressBar";
import { IPackageDetailsItinerary } from "../interface/IPackageDetails.interface";
import { Utensils, MapPin } from "lucide-react";
import RichText from "@/utils/richText";

interface IProps {
  itinerary: IPackageDetailsItinerary[];
  activity?: any[];
  meals?: any[];
}

export const Itenary = ({ itinerary, activity, meals }: IProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const [imageIndex, setImageIndex] = useState<number[]>(() =>
    itinerary.map(() => 0)
  );

  const toggle = (index: number) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const selectImage = (dayIndex: number, imgIndex: number) => {
    setImageIndex((prev) =>
      prev.map((v, i) => (i === dayIndex ? imgIndex : v))
    );
  };

  const scrollIds = itinerary.map((day) => ({
    id: day?.id,
    label: `${day?.day}`,
  }));

  return (
    <div id="itinerary" className="w-full space-y-4">
      <div className="flex items-center gap-4 mb-6 px-2">
        <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">
          Trip Itinerary
        </h2>
        <div className="h-[2px] flex-1 bg-gray-200" />
      </div>

      <ScrollProgressBar sections={scrollIds} />

      {itinerary.map((day, i) => {
        const isOpen = activeIndex === i;
        const dayActivities = activity?.filter(
          (act) => act?.itinerary?.id === day?.id
        );
        const dayMeals = meals?.filter(
          (meal) => meal?.itinerary?.id === day.id
        );

        return (
          <div
            key={day?.id}
            id={day?.id}
            className="scroll-mt-52 overflow-hidden border border-gray-100 rounded-2xl transition-all duration-300 hover:shadow-md"
          >
            <button
              onClick={() => toggle(i)}
              className={`w-full flex items-center justify-between p-5 text-left transition-colors ${isOpen ? "bg-gray-50" : "bg-white"}`}
            >
              <div className="flex items-center gap-5">
                <div
                  className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center font-bold transition-all ${isOpen ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-400"}`}
                >
                  <span className="text-[10px] uppercase opacity-60 leading-none">
                    Day
                  </span>
                  <span className="text-lg leading-none">{day?.day}</span>
                </div>
                <span
                  className={`font-bold text-base md:text-lg uppercase tracking-tight ${isOpen ? "text-gray-900" : "text-gray-600"}`}
                >
                  {day?.title}
                </span>
              </div>
              {isOpen ? (
                <HiChevronUp className="text-gray-900" size={20} />
              ) : (
                <HiChevronDown className="text-gray-400" size={20} />
              )}
            </button>

            {isOpen && (
              <div className="p-5 pt-0 animate-in fade-in slide-in-from-top-2 duration-300">
                {day?.images && day?.images?.length > 0 && (
                  <div className="relative group overflow-hidden rounded-xl mb-6 shadow-inner bg-gray-100">
                    <div className="relative w-full h-64 md:h-[400px]">
                      {day?.images?.map((img, idx) => (
                        <Image
                          key={idx}
                          src={img?.image || "/placeholder.png"}
                          alt="itinerary"
                          fill
                          className={`object-cover transition-opacity duration-700 ${imageIndex[i] === idx ? "opacity-100" : "opacity-0"}`}
                        />
                      ))}
                    </div>

                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                      {day?.images?.map((_, imgIdx) => (
                        <button
                          key={imgIdx}
                          onClick={(e) => {
                            e.stopPropagation();
                            selectImage(i, imgIdx);
                          }}
                          className={`h-1.5 rounded-full transition-all ${imageIndex[i] === imgIdx ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white"}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid lg:grid-cols-1 gap-6 mt-2">
                  <div className="space-y-4 p-4">
                    <RichText
                      content={day?.description || ""}
                      className="text-gray-600 leading-relaxed prose prose-base max-w-none list-disc"
                    />
                  </div>

                  <div className="flex flex-wrap gap-4 mt-4">
                    {dayActivities && dayActivities.length > 0 && (
                      <div className="flex-1 min-w-[280px] bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="flex items-center gap-2 text-[11px] font-black uppercase text-gray-400 tracking-widest mb-3">
                          <MapPin size={14} className="text-primary-500" />{" "}
                          Activity
                        </p>
                        {dayActivities.map((actItem: any) => (
                          <RichText
                            key={actItem?.id}
                            content={actItem.description || ""}
                            className="text-sm font-bold text-gray-700 mb-2 last:mb-0"
                          />
                        ))}
                      </div>
                    )}

                    {dayMeals && dayMeals.length > 0 && (
                      <div className="flex-1 min-w-[280px] bg-secondary-50/30 rounded-xl p-4 border border-secondary-100">
                        <p className="flex items-center gap-2 text-[11px] font-black uppercase text-secondary-700 tracking-widest mb-3">
                          <Utensils size={14} /> Meals
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {dayMeals.map((mealItem) => (
                            <div
                              key={mealItem.id}
                              className="text-sm font-bold text-gray-700"
                            >
                              <span className="text-secondary-600 mr-1">
                                {mealItem.title}:
                              </span>

                              <RichText
                                content={mealItem.description || ""}
                                className="font-medium text-gray-500"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
